import React, { useRef } from 'react'
import styled from '@emotion/styled'

export type BottomSheetSnap = 'collapsed' | 'half' | 'full'

interface MobileBottomSheetProps {
	snap: BottomSheetSnap
	onSnapChange: (snap: BottomSheetSnap) => void
	totalPropiedades: number
	children: React.ReactNode
}

const SNAP_TRANSLATE: Record<BottomSheetSnap, string> = {
	collapsed: 'calc(100% - 80px)',
	half: '50dvh',
	// full: sheet top queda justo bajo el MobileMapHeader (64px + safe-area-inset-top).
	// Con height:100vh el sheet se extiende más allá del viewport por abajo, sin dejar hueco.
	full: 'calc(64px + env(safe-area-inset-top))',
}

const DRAG_THRESHOLD = 60

export const MobileBottomSheet = ({ snap, onSnapChange, totalPropiedades, children }: MobileBottomSheetProps) => {
	const startYRef = useRef<number | null>(null)
	const sheetBodyRef = useRef<HTMLDivElement>(null)

	const handleTouchStart = (e: React.TouchEvent) => {
		// Si el toque viene del body con scroll activo, no iniciar drag
		const target = e.target as Node
		const body = sheetBodyRef.current
		if (body && body.contains(target) && body.scrollTop > 0) {
			startYRef.current = null
			return
		}
		startYRef.current = e.touches[0].clientY
	}

	const handleTouchEnd = (e: React.TouchEvent) => {
		if (startYRef.current === null) return
		const delta = e.changedTouches[0].clientY - startYRef.current
		startYRef.current = null

		// PRUEBA: snap "half" (lista+mapa) deshabilitado — collapsed/full pasan a ser binarios.
		// Para revertir, restaurar los pasos intermedios por 'half' comentados abajo.
		if (delta < -DRAG_THRESHOLD) {
			// Arrastrado hacia arriba → promover snap
			if (snap === 'collapsed') onSnapChange('full')
			// else if (snap === 'half') onSnapChange('full')
		} else if (delta > DRAG_THRESHOLD) {
			// Arrastrado hacia abajo → demover snap
			if (snap === 'full') onSnapChange('collapsed')
			// else if (snap === 'half') onSnapChange('collapsed')
		}
	}

	return (
		<Sheet
			$translate={SNAP_TRANSLATE[snap]}
			onTouchStart={handleTouchStart}
			onTouchEnd={handleTouchEnd}
		>
			<DragHandle />

			{snap === 'collapsed' ? (
				// PRUEBA: antes iba a 'half' (lista+mapa) — ahora salta directo a 'full'.
				<CollapsedPill onClick={() => onSnapChange('full')}>
					Ver {totalPropiedades} propiedades
				</CollapsedPill>
			) : (
				<SheetBody ref={sheetBodyRef}>
					{children}
				</SheetBody>
			)}
		</Sheet>
	)
}

const Sheet = styled.div<{ $translate: string }>`
	position: fixed;
	bottom: 0;
	left: 0;
	right: 0;
	z-index: 300;

	@media (min-width: 768px) {
		display: none;
	}
	height: 100dvh;
	background: #fff;
	border-radius: 16px 16px 0 0;
	box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.12);
	transform: translateY(${({ $translate }) => $translate});
	transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1);
	display: flex;
	flex-direction: column;
	touch-action: none;
`

const DragHandle = styled.div`
	width: 40px;
	height: 5px;
	background: #ccc;
	border-radius: 3px;
	margin: 12px auto 16px;
	flex-shrink: 0;
`

const CollapsedPill = styled.button`
	display: block;
	width: calc(100% - 32px);
	margin: 4px 16px;
	padding: 10px 16px;
	background: #3200C1;
	color: #fff;
	font-family: nunito, sans-serif;
	font-size: 14px;
	font-weight: 700;
	border: none;
	border-radius: 8px;
	cursor: pointer;
	text-align: center;

	&:active {
		background: #2600a8;
	}
`

const SheetBody = styled.div`
	flex: 1;
	overflow-y: auto;
	overflow-x: hidden;
	-webkit-overflow-scrolling: touch;
	padding: 0 16px 32px;
`
